import { useMemo } from 'react';
import { useGetBFAwardsLeaderboardQuery } from 'common/store/modules/api/cfs-api';
import { useInterval } from 'common/react/hooks/use-interval';

const POLLING_DELAY = 30000; // 30 seconds

/**
 * If there is a live season we should get and return the current round number used by selectors here
 *
 * @param {seasonId} string The ID of the season inside the competition, passed from the nav component
 * @returns {number} The current round number or -1 if it is not found
 */
export function useGetCurrentRoundNumber(seasonId) {
    const {
        data: leaderboardData,
        isFetching: isLeaderboardFetching,
        refetch: refetchLeaderboardData
    } = useGetBFAwardsLeaderboardQuery({ seasonId }, { skip: !seasonId });

    // Poll for leaderboard data if live
    useInterval(() => {
        if (
            leaderboardData.status ===
            PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE
        ) {
            refetchLeaderboardData();
        }
    }, POLLING_DELAY);

    const currentRoundNumber = useMemo(() => {
        if (!leaderboardData || isLeaderboardFetching) {
            return -1;
        }

        if (
            leaderboardData.status !==
            PULSE.app.common.CONSTANTS.MATCH_STATUS.LIVE
        ) {
            return -1;
        }

        const allRoundNumbers =
            leaderboardData.leaderboard?.flatMap(
                (item) =>
                    item.roundByRoundTotalVotes
                        ?.flat()
                        .map((i) => i.roundNumber) || []
            ) || [];

        const uniqueRoundNumbers = new Set(allRoundNumbers);

        if (uniqueRoundNumbers.size === 0) {
            return -1;
        }

        return Math.max(...uniqueRoundNumbers);
    }, [
        isLeaderboardFetching,
        leaderboardData?.status,
        leaderboardData?.leaderboard
    ]);

    return currentRoundNumber;
}
